<template>
  <div v-if="selectedFields.length" class="mt-2 space-y-2 rounded border border-slate-600/60 bg-slate-800/30 p-2">
    <div class="text-xs font-semibold uppercase tracking-wide text-slate-400">Action Data</div>
    <div v-for="field in selectedFields" :key="field.key" class="space-y-1">
      <div class="flex min-w-0 items-center gap-2">
        <label class="w-28 shrink-0 truncate text-xs text-gray-300" :title="field.definition.name || field.key">
          {{ field.definition.name || field.key }}
          <span v-if="field.definition.required" class="text-red-400">*</span>
        </label>
        <form-select
          class="w-32 shrink-0 text-xs"
          :modelValue="fieldSource(field)"
          @update:modelValue="setFieldSource(field, $event)">
          <option v-for="source in SERVICE_VALUE_SOURCES" :key="source.value" :value="source.value">
            {{ source.label }}
          </option>
        </form-select>

        <template v-if="fieldSource(field) === 'object'">
          <form-select
            v-if="objectOptions(field).length"
            class="min-w-0 flex-1 text-sm"
            :modelValue="objectValue(field)"
            @update:modelValue="setField(field, $event, 'object')">
            <option value="">— select object property —</option>
            <option v-for="option in objectOptions(field)" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </form-select>
          <form-input
            v-else
            type="text"
            class="min-w-0 flex-1 text-sm"
            placeholder="object.property"
            :modelValue="objectValue(field)"
            @update:modelValue="setField(field, $event, 'object')" />
        </template>

        <form-select
          v-else-if="fieldSource(field) === 'event'"
          class="min-w-0 flex-1 text-sm"
          :modelValue="eventValue(field)"
          @update:modelValue="setEventField(field, $event)">
          <option value="">— select event value —</option>
          <option v-for="option in eventOptions(field)" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </form-select>

        <template v-else>
          <EntitySelector
            v-if="selectorKind(field.definition) === 'entity'"
            class="min-w-0 flex-1"
            :modelValue="displayValue(field)"
            :settings="settings"
            :domain="entityDomainFor(field)"
            :placeholder="placeholder(field.definition)"
            @update:modelValue="setField(field, $event, 'literal')" />
          <div
            v-else-if="literalKind(field) === 'color_rgb'"
            class="flex min-w-0 flex-1 items-center gap-2">
            <color-picker
              class="shrink-0"
              :pureColor="literalColor(field)"
              useType="pure"
              format="hex"
              lang="En"
              shape="circle"
              pickerContainer="body"
              :zIndex="10000"
              @update:pureColor="setLiteralColor(field, $event)"
              @pureColorChange="setLiteralColor(field, $event)" />
            <input
              type="text"
              class="ha-builder-control min-w-0 flex-1 text-sm font-mono"
              placeholder="#FF8000"
              :value="literalColor(field)"
              @input="setLiteralHex(field, $event.target.value)" />
          </div>
          <input
            v-else-if="literalKind(field) === 'number'"
            type="number"
            class="ha-builder-control min-w-0 flex-1 text-sm"
            :min="literalConfig(field).min"
            :max="literalConfig(field).max"
            :step="literalConfig(field).step ?? 'any'"
            :value="displayValue(field)"
            @input="setField(field, $event.target.value === '' ? '' : Number($event.target.value), 'literal')" />
          <label v-else-if="literalKind(field) === 'boolean'" class="inline-flex min-w-0 flex-1 items-center gap-2 text-sm text-slate-200">
            <input type="checkbox" class="accent-cyan-500" :checked="booleanValue(field)" @change="setField(field, $event.target.checked, 'literal')" />
            {{ field.definition.name || field.key }}
          </label>
          <form-select
            v-else-if="literalKind(field) === 'enum'"
            class="min-w-0 flex-1 text-sm"
            :modelValue="displayValue(field)"
            @update:modelValue="setField(field, $event, 'literal')">
            <option value="">— select —</option>
            <option v-for="option in fieldOptions(field.definition)" :key="String(option.value)" :value="option.value">
              {{ option.label }}
            </option>
          </form-select>
          <select
            v-else-if="literalKind(field) === 'array'"
            multiple
            class="ha-builder-control min-w-0 flex-1 text-sm"
            :value="arrayValue(field)"
            @change="setField(field, [...$event.target.selectedOptions].map(option => option.value), 'literal')">
            <option v-for="option in fieldOptions(field.definition)" :key="String(option.value)" :value="option.value">
              {{ option.label }}
            </option>
          </select>
          <textarea
            v-else-if="literalKind(field) === 'object'"
            class="ha-builder-control min-w-0 flex-1 text-sm"
            :value="displayValue(field)"
            @input="setField(field, $event.target.value, 'literal')" />
          <input
            v-else
            type="text"
            class="ha-builder-control min-w-0 flex-1 text-sm"
            :placeholder="placeholder(field.definition)"
            :value="displayValue(field)"
            @input="setField(field, $event.target.value, 'literal')" />
        </template>

        <button
          type="button"
          class="shrink-0 px-1 text-slate-400 hover:text-red-300"
          :aria-label="`Remove ${field.key}`"
          :title="`Remove ${field.key}`"
          @click="removeField(field)">×</button>
      </div>

      <p v-if="field.definition.description && selectorKind(field.definition) !== 'boolean'" class="text-xs text-slate-500">
        {{ field.definition.description }}
      </p>
      <p v-if="fieldError(field)" class="text-xs text-red-400">{{ fieldError(field) }}</p>
    </div>
  </div>
</template>

<script setup>
import { computed, watch } from 'vue';
import EntitySelector from '../EntitySelector.vue';
import {
  fieldSelector,
  fieldOptions,
  initializeServiceFields,
  missingRequiredServiceFields,
  restoreServiceFields,
  serviceFieldDisplayValue,
  serviceFieldEntries,
  serviceFieldColorHex,
  serviceFieldLiteralKind,
  serviceFieldParameter,
  serviceFieldRawValue,
  serviceFieldSource,
  serviceFieldSourceValue,
  SERVICE_VALUE_SOURCES,
  selectorForServiceField,
  validateServiceFieldValue,
} from './serviceFields.js';

const props = defineProps({
  model: { type: Object, required: true },
  service: { type: Object, default: null },
  settings: { type: Object, default: null },
  runtimeVariables: { type: Array, default: () => [] },
  dynamicSuggestions: { type: Array, default: () => [] },
});

const fields = computed(() => serviceFieldEntries(props.service));
const selectedFields = computed(() => {
  const selected = new Set(props.model.actions.flatMap(action =>
    (action.parameters ?? []).map(parameter => parameter.key).filter(Boolean)));
  return fields.value.filter(field => selected.has(field.key));
});

watch(
  () => [props.service?.value, fields.value.map(field => field.key).join('|')],
  () => props.model.actions.forEach(action => {
    restoreServiceFields(action, props.service);
    initializeServiceFields(action, props.service, { includeDefaults: false });
  }),
  { immediate: true },
);

function definitionFor(field) {
  return field.definition;
}

function selectorKind(definition) {
  return fieldSelector(definition).kind;
}

function targetEntityId() {
  const target = props.model.actions[0]?.target?.entity_id;
  if (typeof target === 'string') return target;
  if (Array.isArray(target)) return target[0] ?? '';
  const parameter = props.model.actions[0]?.parameters?.find(item => item.key === 'entity_id');
  return parameter?.value?.toString?.() ?? '';
}

function selectorForField(field) {
  const suggestions = props.dynamicSuggestions
    .filter(suggestion => suggestion.parameter === field.key)
    .map(suggestion => ({ value: suggestion.value, label: suggestion.label }));
  return selectorForServiceField(field.definition, {
    entityId: targetEntityId(),
    runtimeVariables: props.runtimeVariables,
    suggestions,
  });
}

function entityDomainFor(field) {
  const domain = selectorForField(field).filters?.domain ?? '';
  return Array.isArray(domain) ? domain[0] ?? '' : domain;
}

function placeholder(definition) {
  return definition.example == null ? '' : String(definition.example);
}

function currentValue(field) {
  return serviceFieldRawValue(props.model.actions[0], field.key, definitionFor(field));
}

function parameter(field) {
  return serviceFieldParameter(props.model.actions[0], field.key);
}

function fieldSource(field) {
  return serviceFieldSource(parameter(field));
}

function literalKind(field) {
  return serviceFieldLiteralKind(field.definition);
}

function literalConfig(field) {
  return fieldSelector(field.definition).config ?? {};
}

function objectOptions(field) {
  const options = props.dynamicSuggestions
    .filter(suggestion => String(suggestion.value ?? '').startsWith('object.'))
    .map(suggestion => ({ value: suggestion.value, label: suggestion.label }));
  const current = objectValue(field);
  if (current && !options.some(option => option.value === current)) options.unshift({ value: current, label: `${current} (saved)` });
  return options;
}

function objectValue(field) {
  return serviceFieldDisplayValue(currentValue(field));
}

function eventOptions(field) {
  const options = props.runtimeVariables.map(variable => ({
    value: variable.name,
    label: variable.label ? `${variable.label} (${variable.name})` : variable.name,
  }));
  for (const suggestion of props.dynamicSuggestions) {
    const match = /^{{\s*([a-zA-Z_]\w*)\s*}}$/.exec(String(suggestion.value ?? ''));
    if (match && !options.some(option => option.value === match[1])) {
      options.push({ value: match[1], label: suggestion.label ? `${suggestion.label} (${match[1]})` : match[1] });
    }
  }
  const current = eventValue(field);
  if (current && !options.some(option => option.value === current)) options.unshift({ value: current, label: `${current} (saved)` });
  return options;
}

function eventValue(field) {
  const value = currentValue(field);
  const text = value?.toString?.() ?? String(value ?? '');
  const match = /^{{\s*([a-zA-Z_]\w*)\s*}}$/.exec(text);
  return match?.[1] ?? (value?.kind === 'reference' ? value.name : text);
}

function literalColor(field) {
  return serviceFieldColorHex(currentValue(field)).toUpperCase();
}

function booleanValue(field) {
  const value = currentValue(field);
  return value === true || value?.value === true || value === 1 || value === '1' || value === 'true';
}

function arrayValue(field) {
  const value = currentValue(field);
  if (value?.kind === 'list') return value.items.map(String);
  return Array.isArray(value) ? value.map(String) : String(value ?? '').split(',').map(item => item.trim()).filter(Boolean);
}

function displayValue(field) {
  return serviceFieldDisplayValue(currentValue(field));
}

function fieldError(field) {
  if (fieldSource(field) !== 'literal') return '';
  const validation = validateServiceFieldValue(currentValue(field), field.definition);
  if (validation.valid) return '';
  return validation.message || (missingRequiredServiceFields(props.model.actions[0], props.service).includes(field.key)
    ? 'This field is required.'
    : 'Invalid value.');
}

function setField(field, value, source = fieldSource(field)) {
  for (const action of props.model.actions) {
    action.parameters ??= [];
    const parameter = action.parameters.find(item => item.key === field.key);
    const nextValue = serviceFieldSourceValue(value, source, field.definition);
    if (parameter) {
      parameter.source = source;
      parameter.value = nextValue;
    } else action.parameters.push({ key: field.key, source, value: nextValue });
  }
}

function setFieldSource(field, source) {
  if (source === 'event') {
    const first = props.runtimeVariables[0]?.name ?? eventOptions(field)[0]?.value ?? '';
    setEventField(field, first);
    return;
  }
  if (source === 'object') {
    const first = objectOptions(field)[0]?.value ?? objectValue(field);
    setField(field, first, source);
    return;
  }
  const current = currentValue(field);
  const literal = current instanceof Object && ['template', 'reference'].includes(current.kind) ? '' : current;
  setField(field, literal, 'literal');
}

function setEventField(field, value) {
  setField(field, value, 'event');
}

function setLiteralColor(field, value) {
  setField(field, value, 'literal');
}

function setLiteralHex(field, value) {
  const text = String(value ?? '').trim();
  if (/^#?[\da-f]{6}$/i.test(text)) setLiteralColor(field, text);
}

function removeField(field) {
  for (const action of props.model.actions) {
    action.parameters = (action.parameters ?? []).filter(parameter => parameter.key !== field.key);
    if (action.data && Object.prototype.hasOwnProperty.call(action.data, field.key)) {
      const { [field.key]: ignored, ...rest } = action.data;
      action.data = rest;
    }
  }
}
</script>
